/**
 * The invitation token, from the fragment it arrived in to the accept that spends it.
 *
 * This module runs **first** in `main.tsx` — before `posthog.init` and before
 * `ClerkProvider` mounts (U8, R9). The order is the whole point:
 *
 * - PostHog reads `location.href` when it initialises and on every replay snapshot. A
 *   fragment still in the address bar at that moment is a token in a vendor's store.
 * - Clerk's `SignIn routing="hash"` writes its own steps into `location.hash`. A token
 *   sitting there when the provider mounts is a token handed to whatever Clerk does with it.
 *
 * Memory alone is not enough, which is why this keeps a `sessionStorage` entry. A signed-out
 * invitee is sent to Clerk's hosted sign-in and comes back through a redirect, and a redirect
 * does not preserve a fragment. The obvious repair — putting the token in a query parameter
 * so it survives — would post it to a third party's request logs, which is the leak the
 * fragment was chosen to avoid in the first place (KTD9).
 *
 * `sessionStorage` is per tab and dies with it. That is the right lifetime: an invitation is
 * accepted in the tab it was opened in, or not at all.
 */

import { capturePlannerHandoff, isPlannerHandoffPath } from './planner-handoff';

/** One key, read and written in one file, so a stale entry cannot outlive a rename. */
export const INVITE_TOKEN_KEY = 'collab.invite-token';

/**
 * The decline link's token, held apart from the join key on purpose (KTD7).
 *
 * The invitation email carries the same token twice — behind `/join#` and behind
 * `/decline#`. A token held under `INVITE_TOKEN_KEY` is accepted by `App` wherever the
 * teacher lands, so a decline token that reached that key would join the person who asked not
 * to. A separate key is what makes "decline can never become accept" a property of storage
 * rather than of every screen remembering to check.
 */
export const DECLINE_TOKEN_KEY = 'collab.decline-token';

/** The only paths whose fragment is an invitation token, and the key each one is held under. */
const KEY_FOR_PATH: Readonly<Record<string, string>> = {
  '/join': INVITE_TOKEN_KEY,
  '/decline': DECLINE_TOKEN_KEY,
};

/**
 * What a token can look like: `base64url` of 32 random bytes is 43 characters (KTD9).
 *
 * The shape test is not a security check — the Worker decides, and every refusal answers the
 * same thing (R10). It is here so that a fragment Clerk wrote (`#/factor-one`) or a plain
 * `#section` anchor is not mistaken for an invitation and stored as one.
 */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{16,256}$/;

/**
 * The one impure thing this module needs, passed in rather than reached for.
 *
 * `main.tsx` hands it the real window; a test hands it a fake. Nothing here reads a global
 * directly — CLAUDE.md's functional core / imperative shell, applied to the browser edge.
 */
export interface TokenContext {
  /**
   * Which link this is. A fragment is held only on `/join` or `/decline`, and under the key
   * that path names — never under the other one, and never on any other path (KTD7).
   */
  readonly pathname: string;
  readonly hash: string;
  /** Where the address bar should point once the fragment is gone. */
  readonly cleanUrl: string;
  readonly replaceState: (url: string) => void;
  /** Null whenever storage is unavailable — a private window, or blocked site data. */
  readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

/** Pure: the token a fragment carries, or null. */
export function readFragmentToken(hash: string): string | null {
  const value = hash.startsWith('#') ? hash.slice(1) : hash;
  return TOKEN_SHAPE.test(value) ? value : null;
}

const safeStorage = (): TokenContext['storage'] => {
  try {
    return window.sessionStorage;
  } catch {
    // A private window, or site data blocked. The flow still works inside one page load.
    return null;
  }
};

/** The real browser, read once at startup. */
export function browserTokenContext(): TokenContext {
  const { hash, pathname, search } = window.location;
  return {
    pathname,
    hash,
    cleanUrl: `${pathname}${search}`,
    replaceState: (url) => { window.history.replaceState(null, '', url); },
    storage: safeStorage(),
  };
}

/**
 * Strip the fragment, keep the token under the key its path names, and answer with the join
 * token we now hold.
 *
 * Called once, before anything else in `main.tsx`. Calling it again is harmless: with no
 * fragment left it simply returns the stored token, which is exactly what the return leg of
 * a sign-in round trip needs.
 *
 * **The answer is only ever a join token.** A `/decline` fragment is held under the decline
 * key and this returns null for it, because a caller that reads the return value as "this
 * session is a join" must not be handed a decline. A token-shaped fragment on any other path
 * is stripped and dropped: the Worker only ever mints `/join#` and `/decline#` links, so
 * anything else is not one of ours and holding it would only give it a route to the accept.
 *
 * The one other fragment Collab reads is the Planner's, on `/from-planner`. It is handed to
 * `planner-handoff.ts` instead of dropped, and never stored under either token key.
 */
export function captureInviteToken(context: TokenContext = browserTokenContext()): string | null {
  // The Planner's hand-off is not an invitation and never becomes one. Its fragment leaves the
  // address bar first, like a token's, and the bar points at `/` so the router and PostHog
  // see the home page; the hand-off reader validates and holds it (KTD4, KTD5). A held join
  // token is untouched and still answered, so an invitation works with a hand-off pending.
  if (isPlannerHandoffPath(context.pathname)) {
    context.replaceState('/');
    capturePlannerHandoff(context.hash, context.storage);
    return heldInviteToken(context);
  }

  const fromFragment = readFragmentToken(context.hash);

  // The fragment goes first, and unconditionally when there was one — before the token is
  // stored, before analytics starts, before Clerk mounts.
  if (context.hash.length > 0) context.replaceState(context.cleanUrl);

  const key = KEY_FOR_PATH[context.pathname];
  if (fromFragment !== null && key !== undefined) {
    try {
      context.storage?.setItem(key, fromFragment);
    } catch {
      // Storage refused. A join token is still returned, so an already-signed-in invitee
      // accepts in this page load; only the sign-in round trip is lost.
    }
    if (key === INVITE_TOKEN_KEY) return fromFragment;
  }

  return heldInviteToken(context);
}

/** Pure over the context: the well-formed token held under `key`, or null. */
function heldUnder(key: string, context: TokenContext): string | null {
  try {
    const stored = context.storage?.getItem(key) ?? null;
    return stored !== null && TOKEN_SHAPE.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

function clearKey(key: string, context: TokenContext): void {
  try {
    context.storage?.removeItem(key);
  } catch {
    // Nothing to do, and nothing worth telling a teacher about.
  }
}

/** The token being held across a sign-in, or null. */
export function heldInviteToken(context: TokenContext = browserTokenContext()): string | null {
  return heldUnder(INVITE_TOKEN_KEY, context);
}

/**
 * The decline link's token, or null. Read by `App` and handed only to `DeclinePage`, which
 * posts it to the decline route and nowhere else.
 */
export function heldDeclineToken(context: TokenContext = browserTokenContext()): string | null {
  return heldUnder(DECLINE_TOKEN_KEY, context);
}

/**
 * Spend the decline token. Called once the decline has answered, or the teacher cancelled —
 * a decline link opened and walked away from should not keep offering itself in this tab.
 */
export function clearDeclineToken(context: TokenContext = browserTokenContext()): void {
  clearKey(DECLINE_TOKEN_KEY, context);
}

/**
 * Spend it. Called once the accept has answered — either way.
 *
 * A refused token is cleared too. It is single-use by the Worker's rules whatever happened
 * (KTD9), and a token kept after a refusal is a token that survives into the next thing this
 * tab does.
 */
export function clearInviteToken(context: TokenContext = browserTokenContext()): void {
  clearKey(INVITE_TOKEN_KEY, context);
}
